"use client";

import { useEffect } from "react";

// Ships uncaught browser errors and unhandled promise rejections back to
// /api/client-error, which logs them to journald on the VPS. Pure web-platform
// APIs — no Sentry/third-party SDK. Mounted once in the root layout.
const ENDPOINT = "/api/client-error";
const MAX_REPORTS_PER_LOAD = 25; // backstop against error loops flooding the box

export default function ClientErrorReporter() {
  useEffect(() => {
    let sent = 0;
    const seen = new Set<string>();

    function report(payload: Record<string, unknown>) {
      if (sent >= MAX_REPORTS_PER_LOAD) return;
      // Dedupe identical errors within a page load.
      const sig = `${payload.message}|${payload.source}|${payload.line}`;
      if (seen.has(sig)) return;
      seen.add(sig);
      sent++;

      const body = JSON.stringify({
        ...payload,
        url: window.location.href,
        ts: new Date().toISOString(),
      });

      try {
        // sendBeacon survives navigation/unload; fall back to keepalive fetch.
        if (navigator.sendBeacon) {
          navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
        } else {
          void fetch(ENDPOINT, { method: "POST", body, keepalive: true, headers: { "Content-Type": "application/json" } });
        }
      } catch {
        // Never let the reporter itself throw.
      }
    }

    function onError(e: ErrorEvent) {
      report({
        kind: "error",
        message: e.message,
        source: e.filename,
        lineno: e.lineno,
        colno: e.colno,
        stack: e.error?.stack,
      });
    }

    function onRejection(e: PromiseRejectionEvent) {
      const reason = e.reason;
      report({
        kind: "unhandledrejection",
        message: reason?.message ?? String(reason),
        stack: reason?.stack,
      });
    }

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
